import { applicationsAttributesIDs } from 'app/sm/team_viewing_preference/applications-attributes.constants'
import { isApplicationRentalLedgerRequiredSettingEnabled } from 'config/features'

type TeamApplicationSettings = Record<string, string | boolean | undefined>
type RentalHistoryEntry = {
  guidID?: string | undefined
  propertyArrangement?: number | string | undefined
  isCurrentAddress?: boolean | undefined
  is_current_address?: boolean | undefined
  IsCurrentAddress?: boolean | undefined
  startDate?: string | Date | null | undefined
  endDate?: string | Date | null | undefined
  createdAt?: string | Date | null | undefined
  updatedAt?: string | Date | null | undefined
}

const DRAFT_RENTAL_HISTORY_GUID = '__draft_rental_history__'
const LEDGER_HIDDEN_ARRANGEMENTS = [5, 7, 10]

export const isRentalLedgerRequiredByAgencyPolicy = (
  teamApplicationSettings?: TeamApplicationSettings,
  // Defaults to true because renter-side callers don't have access to the
  // agency's premier status. The settings page already gates enabling this
  // setting by premier status, so the team setting value itself is the
  // authoritative check from the renter side.
  isAccountPremier = true,
): boolean => {
  if (!isApplicationRentalLedgerRequiredSettingEnabled(isAccountPremier)) {
    return false
  }

  if (!teamApplicationSettings) {
    return false
  }

  const value =
    teamApplicationSettings[
      applicationsAttributesIDs.applicationRentalLedgerRequired
    ]

  return value === true || value === 'true'
}

export const isRentalLedgerEligibleArrangement = (
  propertyArrangement?: number | string,
): boolean => {
  if (propertyArrangement === undefined || propertyArrangement === null) {
    return false
  }

  const parsedArrangement = Number(propertyArrangement)
  if (!Number.isFinite(parsedArrangement) || parsedArrangement <= 0) {
    return false
  }

  return !LEDGER_HIDDEN_ARRANGEMENTS.includes(parsedArrangement)
}

export const isCurrentAddressHistory = (
  rentalHistory?: RentalHistoryEntry,
): boolean =>
  rentalHistory?.isCurrentAddress === true ||
  rentalHistory?.is_current_address === true ||
  rentalHistory?.IsCurrentAddress === true

export const getCurrentOrMostRecentRentalLedgerRequiredHistory = (
  rentalHistory: RentalHistoryEntry[] = [],
): RentalHistoryEntry | null => {
  if (!Array.isArray(rentalHistory) || rentalHistory.length === 0) {
    return null
  }

  const currentEligibleAddress = rentalHistory.find(
    (history) =>
      isCurrentAddressHistory(history) &&
      isRentalLedgerEligibleArrangement(history?.propertyArrangement),
  )
  if (currentEligibleAddress) {
    return currentEligibleAddress
  }

  const eligibleHistories = rentalHistory.filter((history) =>
    isRentalLedgerEligibleArrangement(history?.propertyArrangement),
  )
  if (eligibleHistories.length === 0) {
    return null
  }

  const getTimestamp = (value?: string | Date | null): number => {
    if (!value) {
      return Number.NaN
    }

    const parsed = new Date(value).getTime()
    return Number.isFinite(parsed) ? parsed : Number.NaN
  }

  const getSortKey = (history: RentalHistoryEntry): number => {
    const endDateTimestamp = getTimestamp(history?.endDate)
    if (Number.isFinite(endDateTimestamp)) {
      return endDateTimestamp
    }

    const startDateTimestamp = getTimestamp(history?.startDate)
    if (Number.isFinite(startDateTimestamp)) {
      return startDateTimestamp
    }

    const updatedAtTimestamp = getTimestamp(history?.updatedAt)
    if (Number.isFinite(updatedAtTimestamp)) {
      return updatedAtTimestamp
    }

    const createdAtTimestamp = getTimestamp(history?.createdAt)
    if (Number.isFinite(createdAtTimestamp)) {
      return createdAtTimestamp
    }

    return Number.NEGATIVE_INFINITY
  }

  const hasAnySortableDate = eligibleHistories.some((history) =>
    Number.isFinite(getSortKey(history)),
  )

  if (!hasAnySortableDate) {
    return eligibleHistories[0] || null
  }

  return eligibleHistories.reduce((mostRecent, history) => {
    return getSortKey(history) > getSortKey(mostRecent) ? history : mostRecent
  })
}

type DraftRentalHistoryForPolicy = {
  guidID?: string | undefined
  propertyArrangement?: number | string | undefined
  isCurrentAddress?: boolean | undefined
  startDate?: string | Date | null | undefined
  endDate?: string | Date | null | undefined
}

type DraftLedgerRequirementParams = {
  rentalHistory?: RentalHistoryEntry[]
  editingRentalHistoryGUID?: string | undefined
  draftRentalHistory?: DraftRentalHistoryForPolicy
}

export const isRentalLedgerRequiredForDraftAddressByPolicy = ({
  rentalHistory = [],
  editingRentalHistoryGUID,
  draftRentalHistory,
}: DraftLedgerRequirementParams): boolean => {
  if (!draftRentalHistory) {
    return false
  }

  if (
    !isRentalLedgerEligibleArrangement(draftRentalHistory.propertyArrangement)
  ) {
    return false
  }

  if (isCurrentAddressHistory(draftRentalHistory)) {
    return true
  }

  const draftGUID =
    editingRentalHistoryGUID ||
    draftRentalHistory.guidID ||
    DRAFT_RENTAL_HISTORY_GUID

  const normalizedDraftHistory: RentalHistoryEntry = {
    guidID: draftGUID,
    propertyArrangement: draftRentalHistory.propertyArrangement,
    isCurrentAddress: draftRentalHistory.isCurrentAddress,
    startDate: draftRentalHistory.startDate,
    endDate: draftRentalHistory.endDate,
  }

  const normalizedRentalHistory = Array.isArray(rentalHistory)
    ? [...rentalHistory]
    : []

  const draftIndex = normalizedRentalHistory.findIndex(
    (history) => history?.guidID === draftGUID,
  )

  if (draftIndex >= 0) {
    normalizedRentalHistory[draftIndex] = normalizedDraftHistory
  } else {
    normalizedRentalHistory.push(normalizedDraftHistory)
  }

  const requiredRentalHistory =
    getCurrentOrMostRecentRentalLedgerRequiredHistory(normalizedRentalHistory)

  return requiredRentalHistory?.guidID === draftGUID
}
